import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  FaList,
  FaChartPie,
  FaHome,
  FaTh,
  FaBox,
  FaTags,
  FaSignOutAlt,
  FaChevronRight,
  FaPlus,
  FaTimes,
  FaBars,
  FaArrowLeft,
  FaArrowRight,
  FaArrowDown,
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaListOl,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaLink,
  FaImage,
} from "react-icons/fa";
import { AuthContext } from "../components/AuthContext";
import { API_URL } from "../constant";

const Products = () => {
  const navigate = useNavigate();
  const { authToken } = useContext(AuthContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [imagePreviews, setImagePreviews] = useState([]);
  const [editingProductId, setEditingProductId] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [viewProduct, setViewProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);
  const [selectedImage, setSelectedImage] = useState(null);
  const [categoryImagePreview, setCategoryImagePreview] = useState("");

  // Text editor ref
  const editorRef = useRef(null);

  // --- Form state for add/edit product ---
  const [formData, setFormData] = useState({
    name: "",
    category_name: "",
    subcategory_name: "",
    stock_quantity: "",
    price: "",
    discounted_price: "",
    description: "",
    images: [],
    combinations: [],
  });

  // --- HTML Text Editor Functions ---
  const wrapSelectedText = (startTag, endTag = "") => {
    const textarea = editorRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    // If no text is selected, insert the tags with cursor in middle
    if (selectedText.length === 0) {
      const newText = startTag + endTag;
      const newValue =
        textarea.value.substring(0, start) +
        newText +
        textarea.value.substring(end);

      setFormData((prev) => ({ ...prev, description: newValue }));

      // Set cursor position between the tags
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + startTag.length,
          start + startTag.length
        );
      }, 0);
    } else {
      // If text is selected, wrap the selected text
      const newText = startTag + selectedText + endTag;
      const newValue =
        textarea.value.substring(0, start) +
        newText +
        textarea.value.substring(end);

      setFormData((prev) => ({ ...prev, description: newValue }));

      // Set selection to the newly formatted text
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start, start + newText.length);
      }, 0);
    }
  };

  const executeCommand = (command) => {
    const textarea = editorRef.current;
    if (!textarea) return;

    switch (command) {
      case "bold":
        wrapSelectedText("<b>", "</b>");
        break;
      case "italic":
        wrapSelectedText("<i>", "</i>");
        break;
      case "underline":
        wrapSelectedText("<u>", "</u>");
        break;
      case "insertUnorderedList":
        insertList("unordered");
        break;
      case "insertOrderedList":
        insertList("ordered");
        break;
      default:
        break;
    }
  };

  const insertList = (type) => {
    const textarea = editorRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    let listText = "";
    if (selectedText) {
      const lines = selectedText.split("\n");
      listText = lines
        .map((line) => {
          if (line.trim() === "") return "";
          return type === "unordered" ? `<li>${line}</li>` : `<li>${line}</li>`;
        })
        .join("\n");
      listText =
        type === "unordered"
          ? `<ul>\n${listText}\n</ul>`
          : `<ol>\n${listText}\n</ol>`;
    } else {
      listText =
        type === "unordered"
          ? "<ul>\n<li></li>\n</ul>"
          : "<ol>\n<li></li>\n</ol>";
    }

    const newValue =
      textarea.value.substring(0, start) +
      listText +
      textarea.value.substring(end);

    setFormData((prev) => ({ ...prev, description: newValue }));

    setTimeout(() => {
      textarea.focus();
      if (selectedText) {
        textarea.setSelectionRange(start, start + listText.length);
      } else {
        const newPosition = start + listText.indexOf("<li>") + 4;
        textarea.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const insertImage = () => {
    const url = prompt("Enter image URL:");
    if (url) {
      const alt = prompt("Enter image description (optional):") || "Image";
      const imgTag = `<img src="${url}" alt="${alt}" style="max-width: 100%; height: auto;" />`;
      insertAtCursor(imgTag);
    }
  };

  const createLink = () => {
    const url = prompt("Enter URL:");
    if (!url) return;

    const text = prompt("Enter link text (optional):") || url;
    const linkTag = `<a href="${url}" target="_blank">${text}</a>`;

    const textarea = editorRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);

    // If text is selected, use it as link text
    const finalLink = selectedText
      ? `<a href="${url}" target="_blank">${selectedText}</a>`
      : linkTag;

    const newValue =
      textarea.value.substring(0, start) +
      finalLink +
      textarea.value.substring(end);

    setFormData((prev) => ({ ...prev, description: newValue }));

    setTimeout(() => {
      textarea.focus();
      const newPosition = start + finalLink.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const insertAtCursor = (text) => {
    const textarea = editorRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const newValue =
      textarea.value.substring(0, start) + text + textarea.value.substring(end);

    setFormData((prev) => ({ ...prev, description: newValue }));

    setTimeout(() => {
      textarea.focus();
      const newPosition = start + text.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleEditorInput = (e) => {
    setFormData((prev) => ({ ...prev, description: e.target.value }));
  };

  // Convert HTML to readable text for editing
  const htmlToText = (html) => {
    if (!html) return "";

    // Convert HTML back to plain text with EXACT line breaks preserved
    let text = html
      .replace(/<br\s*\/?>\s*<br\s*\/?>/gi, "\n\n") // Convert double <br> to double line breaks
      .replace(/<br\s*\/?>/gi, "\n") // Convert single <br> to single line breaks
      .replace(/<\/p>/gi, "\n\n") // Convert paragraph ends to double line breaks
      .replace(/<\/div>/gi, "\n\n") // Convert div ends to double line breaks
      .replace(/<p>/gi, "") // Remove paragraph starts
      .replace(/<div[^>]*>/gi, "") // Remove div starts
      .replace(/<[^>]*>/g, "") // Remove all other HTML tags
      .replace(/&nbsp;/g, " ") // Convert non-breaking spaces to regular spaces
      .replace(/&amp;/g, "&") // Convert HTML entities
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    return text;
  };

  // Convert text back to HTML for storage
  const textToHtml = (text) => {
    if (!text) return "";

    // Preserve all line breaks exactly as they appear
    let html = text
      .replace(/\r\n/g, "\n") // Normalize line endings
      .replace(/\r/g, "\n") // Normalize line endings
      .split("\n") // Split by line breaks
      .map((line) => {
        if (line.trim() === "") {
          return "<br>"; // Empty lines become single line breaks
        }
        return line + "<br>"; // Non-empty lines get line break
      })
      .join("");

    // Remove trailing <br> if exists
    html = html.replace(/<br>$/g, "");

    return html;
  };
  // Initialize editor when form opens
  useEffect(() => {
    if (showAddCard && editorRef.current) {
      // Convert HTML to editable text when opening editor
      const editableText = htmlToText(formData.description);
      setFormData((prev) => ({ ...prev, description: editableText }));

      // Focus the editor when the form opens
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
        }
      }, 100);
    }
  }, [showAddCard]);

  // Convert back to HTML before saving
  const prepareDescriptionForSave = (description) => {
    return textToHtml(description);
  };

  // --- Rest of your existing code remains the same ---
  // [The rest of your existing code: Stepper Modal State, Validation Functions, API Fetch Functions, etc.]

  // --- Stepper Modal State ---
  const [currentStep, setCurrentStep] = useState(1);

  // --- Validation Functions ---
  const validateStep = (step) => {
    if (step === 1) {
      return (
        String(formData.name).trim() &&
        String(formData.category_name).trim() &&
        String(formData.subcategory_name).trim() &&
        String(formData.description).trim()
      );
    } else if (step === 2) {
      return (
        String(formData.stock_quantity).trim() &&
        String(formData.price).trim() &&
        String(formData.discounted_price).trim()
      );
    } else if (step === 3) {
      return formData.images.length > 0;
    }
    return true;
  };

  const canAccessStep = (targetStep) => {
    if (targetStep <= currentStep) return true;
    for (let step = currentStep; step < targetStep; step++) {
      if (!validateStep(step)) return false;
    }
    return true;
  };

  // --- API Fetch Functions ---
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch categories");
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [authToken]);

  const fetchSubcategories = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/categories/subcategories/all`,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch subcategories");
      const data = await response.json();
      setSubcategories(data);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  }, [authToken]);

  const fetchProducts = useCallback(async () => {
    try {
      if (!authToken) {
        console.error("No auth token provided. Cannot fetch products.");
        return;
      }
      const response = await fetch(`${API_URL}/api/products`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch products: ${response.status} ${response.statusText}. ${errorText}`
        );
      }
      const data = await response.json();
      setProducts(data.products || data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }, [authToken]);

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
    fetchProducts();
  }, [authToken, fetchCategories, fetchSubcategories, fetchProducts]);

  // --- Form Input Handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Update category image preview when category name changes
    if (name === "category_name") {
      const matchedCategory = categories.find(
        (cat) => cat.category_name.toLowerCase() === value.toLowerCase()
      );
      if (matchedCategory && matchedCategory.image) {
        setCategoryImagePreview(`${API_URL}/storage/${matchedCategory.image}`);
      } else {
        setCategoryImagePreview("");
      }
    }
  };

  // --- Category Image Upload ---
  const handleCategoryImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, category_image: file }));
      setCategoryImagePreview(URL.createObjectURL(file));
    }
  };

  // --- Image Upload for Product Images ---
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));

    const newPreviews = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setImagePreviews((prev) => [...prev, ...newPreviews]);

    e.target.value = null;
  };

  const removeImage = (index) => {
    const updatedImages = [...formData.images];
    URL.revokeObjectURL(imagePreviews[index].preview);
    updatedImages.splice(index, 1);
    setFormData((prev) => ({ ...prev, images: updatedImages }));

    const updatedPreviews = [...imagePreviews];
    updatedPreviews.splice(index, 1);
    setImagePreviews(updatedPreviews);
  };

  // --- Combination/Variant Handlers ---
  const handleCombinationChange = (combIndex, field, value) => {
    const updatedCombinations = [...formData.combinations];
    updatedCombinations[combIndex] = {
      ...updatedCombinations[combIndex],
      [field]: value,
    };
    setFormData((prev) => ({ ...prev, combinations: updatedCombinations }));
  };

  const handleAttributeChange = (combIndex, attrIndex, field, value) => {
    const updatedCombinations = [...formData.combinations];
    if (!updatedCombinations[combIndex].attributes) {
      updatedCombinations[combIndex].attributes = [];
    }

    // Ensure the attribute array has enough elements
    while (updatedCombinations[combIndex].attributes.length <= attrIndex) {
      updatedCombinations[combIndex].attributes.push({
        name: "",
        value: "",
        color_image: null,
        color_image_preview: "",
      });
    }

    updatedCombinations[combIndex].attributes[attrIndex] = {
      ...updatedCombinations[combIndex].attributes[attrIndex],
      [field]: value,
    };

    setFormData((prev) => ({ ...prev, combinations: updatedCombinations }));
  };

  const handleColorImageUpload = (combIndex, attrIndex, e) => {
    const file = e.target.files[0];
    if (file) {
      const updatedCombinations = [...formData.combinations];
      if (!updatedCombinations[combIndex].attributes) {
        updatedCombinations[combIndex].attributes = [];
      }

      while (updatedCombinations[combIndex].attributes.length <= attrIndex) {
        updatedCombinations[combIndex].attributes.push({
          name: "",
          value: "",
          color_image: null,
          color_image_preview: "",
        });
      }

      updatedCombinations[combIndex].attributes[attrIndex] = {
        ...updatedCombinations[combIndex].attributes[attrIndex],
        color_image: file,
        color_image_preview: URL.createObjectURL(file),
      };

      setFormData((prev) => ({ ...prev, combinations: updatedCombinations }));
    }
  };

  const removeColorImage = (combIndex, attrIndex) => {
    const updatedCombinations = [...formData.combinations];
    if (
      updatedCombinations[combIndex].attributes &&
      updatedCombinations[combIndex].attributes[attrIndex]
    ) {
      if (
        updatedCombinations[combIndex].attributes[attrIndex].color_image_preview
      ) {
        URL.revokeObjectURL(
          updatedCombinations[combIndex].attributes[attrIndex]
            .color_image_preview
        );
      }

      updatedCombinations[combIndex].attributes[attrIndex] = {
        ...updatedCombinations[combIndex].attributes[attrIndex],
        color_image: null,
        color_image_preview: "",
      };

      setFormData((prev) => ({ ...prev, combinations: updatedCombinations }));
    }
  };

  const addAttributeToCombination = (combIndex) => {
    const updatedCombinations = [...formData.combinations];
    if (!updatedCombinations[combIndex].attributes) {
      updatedCombinations[combIndex].attributes = [];
    }

    updatedCombinations[combIndex].attributes.push({
      name: "",
      value: "",
      color_image: null,
      color_image_preview: "",
    });

    setFormData((prev) => ({ ...prev, combinations: updatedCombinations }));
  };

  const removeAttributeFromCombination = (combIndex, attrIndex) => {
    const updatedCombinations = [...formData.combinations];
    if (updatedCombinations[combIndex].attributes) {
      // Clean up image URL if exists
      if (
        updatedCombinations[combIndex].attributes[attrIndex].color_image_preview
      ) {
        URL.revokeObjectURL(
          updatedCombinations[combIndex].attributes[attrIndex]
            .color_image_preview
        );
      }

      updatedCombinations[combIndex].attributes.splice(attrIndex, 1);
      setFormData((prev) => ({ ...prev, combinations: updatedCombinations }));
    }
  };

  const addCombination = () => {
    setFormData((prev) => ({
      ...prev,
      combinations: [
        ...prev.combinations,
        {
          attributes: [],
          price: "",
          discounted_price: "",
          stock_quantity: "",
          sku: "",
        },
      ],
    }));
  };

  const removeCombination = (index) => {
    const updatedCombinations = formData.combinations.filter(
      (_, i) => i !== index
    );

    // Clean up all image URLs in the removed combination
    if (formData.combinations[index].attributes) {
      formData.combinations[index].attributes.forEach((attr) => {
        if (attr.color_image_preview) {
          URL.revokeObjectURL(attr.color_image_preview);
        }
      });
    }

    setFormData((prev) => ({ ...prev, combinations: updatedCombinations }));
  };

  const resetFormData = () => {
    // Clean up image previews
    imagePreviews.forEach((preview) => {
      URL.revokeObjectURL(preview.preview);
    });

    // Clean up attribute image previews
    formData.combinations.forEach((combination) => {
      if (combination.attributes) {
        combination.attributes.forEach((attr) => {
          if (attr.color_image_preview) {
            URL.revokeObjectURL(attr.color_image_preview);
          }
        });
      }
    });

    // Clean up category image preview
    if (categoryImagePreview) {
      URL.revokeObjectURL(categoryImagePreview);
    }

    setFormData({
      name: "",
      category_name: "",
      subcategory_name: "",
      stock_quantity: "",
      price: "",
      discounted_price: "",
      description: "",
      images: [],
      combinations: [],
    });
    setImagePreviews([]);
    setCategoryImagePreview("");
    setCurrentStep(1);
  };

  // --- CRUD Operations ---
  const handleAdd = () => {
    const {
      name,
      category_name,
      subcategory_name,
      stock_quantity,
      price,
      discounted_price,
      description,
      images,
      combinations,
    } = formData;

    if (
      !name ||
      !category_name ||
      !subcategory_name ||
      !stock_quantity ||
      !price ||
      !discounted_price ||
      !description ||
      images.length === 0
    ) {
      setModalMessage("All fields are required.");
      setModalType("error");
      setShowModal(true);
      return;
    }

    const data = new FormData();
    data.append("name", name);
    data.append("category_name", category_name);
    data.append("subcategory_name", subcategory_name);
    data.append("stock_quantity", stock_quantity);
    data.append("price", price);
    data.append("discounted_price", discounted_price);

    // Convert description to HTML before saving
    const htmlDescription = prepareDescriptionForSave(description);
    data.append("description", htmlDescription);

    // Append category image if provided
    if (formData.category_image) {
      data.append("category_image", formData.category_image);
    }

    // Append product images
    images.forEach((file) => {
      data.append("images[]", file);
    });

    // Append combinations if any
    if (combinations.length > 0) {
      combinations.forEach((combination, index) => {
        data.append(
          `combinations[${index}][price]`,
          combination.price || formData.price
        );
        data.append(
          `combinations[${index}][discounted_price]`,
          combination.discounted_price ||
            combination.price ||
            formData.discounted_price
        );
        data.append(
          `combinations[${index}][stock_quantity]`,
          combination.stock_quantity || formData.stock_quantity
        );
        if (combination.sku) {
          data.append(`combinations[${index}][sku]`, combination.sku);
        }

        // Append attributes
        if (combination.attributes && combination.attributes.length > 0) {
          combination.attributes.forEach((attr, attrIndex) => {
            if (attr.name && attr.value) {
              data.append(
                `combinations[${index}][attributes][${attr.name}]`,
                attr.value
              );

              // Append color image for color attributes
              if (attr.name.toLowerCase() === "color" && attr.color_image) {
                data.append(
                  `combinations[${index}][color_image]`,
                  attr.color_image
                );
              }
            }
          });
        }
      });
    }

    fetch(`${API_URL}/api/products`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: data,
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(text || "Failed to add product");
          });
        }
        return response.json();
      })
      .then((newProduct) => {
        fetchProducts();
        resetFormData();
        setShowAddCard(false);
        setModalMessage("Product added successfully!");
        setModalType("success");
        setShowModal(true);
      })
      .catch((error) => {
        console.error("Error adding product:", error);
        setModalMessage("Error adding product: " + error.message);
        setModalType("error");
        setShowModal(true);
      });
  };

  const handleEdit = (product) => {
    setEditingProductId(product.id);

    // Format combinations for editing
    const combinations = product.variants
      ? product.variants.map((variant) => {
          const attributes = [];
          if (variant.attributes) {
            Object.entries(variant.attributes).forEach(([name, value]) => {
              // Check if there's a color image for this color
              const colorImage =
                product.color_images &&
                product.color_images[value.toLowerCase()];
              attributes.push({
                name,
                value,
                color_image: null,
                color_image_preview: colorImage
                  ? colorImage.image_url.startsWith("http")
                    ? colorImage.image_url
                    : `${API_URL}/storage/${
                        colorImage.image_path || colorImage.image_url
                      }`
                  : "",
              });
            });
          }

          return {
            attributes,
            price: variant.price,
            discounted_price: variant.discounted_price,
            stock_quantity: variant.stock_quantity,
            sku: variant.sku || "",
          };
        })
      : [];

    // Handle existing images
    const existingImages = product.images || [];
    const imagePreviewsData = existingImages.map((img) => ({
      file: img,
      preview: `${API_URL}/storage/${img}`,
      isExisting: true,
    }));

    // Convert HTML description to editable text
    const editableDescription = htmlToText(product.description);

    setFormData({
      name: product.name,
      category_name: product.category || product.category_name,
      subcategory_name: product.subcategory || product.subcategory_name,
      stock_quantity: product.stock_quantity,
      price: product.price,
      discounted_price: product.discounted_price,
      description: editableDescription,
      images: existingImages,
      combinations: combinations,
    });

    setImagePreviews(imagePreviewsData);

    // Set category image preview
    if (product.category_image) {
      setCategoryImagePreview(`${API_URL}/storage/${product.category_image}`);
    }

    setShowAddCard(true);
    setCurrentStep(1);
  };

  const handleUpdate = async () => {
    if (!editingProductId) return;

    const {
      name,
      category_name,
      subcategory_name,
      stock_quantity,
      price,
      discounted_price,
      description,
      images,
      combinations,
    } = formData;

    const data = new FormData();
    data.append("name", name);
    data.append("category_name", category_name);
    data.append("subcategory_name", subcategory_name);
    data.append("stock_quantity", stock_quantity);
    data.append("price", price);
    data.append("discounted_price", discounted_price);

    // Convert description to HTML before saving
    const htmlDescription = prepareDescriptionForSave(description);
    data.append("description", htmlDescription);
    data.append("_method", "PUT");

    // Append category image if provided
    if (formData.category_image) {
      data.append("category_image", formData.category_image);
    }

    // Append existing images that are strings (URLs)
    const existingImages = images.filter((img) => typeof img === "string");
    data.append("existing_images", JSON.stringify(existingImages));

    // Append new images
    images.forEach((file) => {
      if (file instanceof File) {
        data.append("images[]", file);
      }
    });

    // Append combinations
    if (combinations.length > 0) {
      combinations.forEach((combination, index) => {
        data.append(
          `combinations[${index}][price]`,
          combination.price || formData.price
        );
        data.append(
          `combinations[${index}][discounted_price]`,
          combination.discounted_price ||
            combination.price ||
            formData.discounted_price
        );
        data.append(
          `combinations[${index}][stock_quantity]`,
          combination.stock_quantity || formData.stock_quantity
        );
        if (combination.sku) {
          data.append(`combinations[${index}][sku]`, combination.sku);
        }

        // Append attributes
        if (combination.attributes && combination.attributes.length > 0) {
          combination.attributes.forEach((attr, attrIndex) => {
            if (attr.name && attr.value) {
              data.append(
                `combinations[${index}][attributes][${attr.name}]`,
                attr.value
              );

              // Append color image for color attributes
              if (attr.name.toLowerCase() === "color" && attr.color_image) {
                data.append(
                  `combinations[${index}][color_image]`,
                  attr.color_image
                );
              }
            }
          });
        }
      });
    }

    try {
      const response = await fetch(
        `${API_URL}/api/products/${editingProductId}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: data,
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to update product");
      }
      await response.json();
      fetchProducts();
      resetFormData();
      setEditingProductId(null);
      setShowAddCard(false);
      setModalMessage("Product updated successfully!");
      setModalType("success");
      setShowModal(true);
    } catch (error) {
      console.error("Error updating product:", error);
      setModalMessage("Error updating product: " + error.message);
      setModalType("error");
      setShowModal(true);
    }
  };

  // [Rest of your existing code: handleDelete, confirmDeleteProduct, etc.]

  const handleDelete = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const confirmDeleteProduct = () => {
    fetch(`${API_URL}/api/products/${productToDelete.id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(text || "Error deleting product");
          });
        }
        fetchProducts();
        setShowDeleteModal(false);
        setModalMessage("Product deleted successfully!");
        setModalType("success");
        setShowModal(true);
      })
      .catch((error) => {
        console.error("Error deleting product:", error);
        setModalMessage("Error deleting product: " + error.message);
        setModalType("error");
        setShowModal(true);
      });
  };

  const cancelDelete = () => setShowDeleteModal(false);
  const handleViewValues = (product) => setViewProduct(product);
  const closeViewModal = () => setViewProduct(null);

  // --- Filtering & Pagination ---
  const filteredProducts = products.filter(
    ({ name, description, category, category_name }) =>
      (name || "").toLowerCase().includes(search.toLowerCase()) ||
      (description || "").toLowerCase().includes(search.toLowerCase()) ||
      (category || category_name || "")
        .toLowerCase()
        .includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  // Update the text editor section in the JSX to show the new formatting help
  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 bg-white shadow-md">
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          <FaBars className="text-2xl text-gray-600" />
        </button>
        <span className="text-xl font-bold text-gray-700">Products</span>
      </div>

      <div className="flex flex-col md:flex-row min-h-screen overflow-hidden">
        {/* Sidebar - unchanged */}
        <aside
          className={`${
            sidebarOpen ? "block" : "hidden"
          } md:block w-full md:w-64 bg-white shadow-md flex flex-col`}
        >
          <div className="hidden md:flex items-center justify-center py-6">
            <div className="text-3xl font-bold text-gray-600 flex items-center">
              <FaChartPie style={{ fontSize: "48px", marginRight: "10px" }} />
              <span
                className="text-2xl md:text-3xl font-bold"
                style={{ color: "gray-600" }}
              >
                Dashboard
              </span>
            </div>
          </div>
          {/* Top Section */}
          <nav className="mt-6 flex flex-col flex-grow text-gray-600">
            <ul>
              {[
                {
                  name: "Home",
                  icon: (
                    <FaHome className="mr-2" style={{ fontSize: "20px" }} />
                  ),
                  link: "/Dashboard",
                },
                {
                  name: "Categories",
                  icon: (
                    <FaList className="mr-2" style={{ fontSize: "20px" }} />
                  ),
                  link: "/Categories",
                },
                {
                  name: "Subcategories",
                  icon: <FaTh className="mr-2" style={{ fontSize: "20px" }} />,
                  link: "/Subcategories",
                },
                {
                  name: "Products",
                  icon: <FaBox className="mr-2" style={{ fontSize: "20px" }} />,
                  link: "/Products",
                },
                {
                  name: "Attributes",
                  icon: (
                    <FaTags className="mr-2" style={{ fontSize: "20px" }} />
                  ),
                  link: "/Attributes",
                },
              ].map((item, idx) => (
                <li
                  key={idx}
                  onClick={() => navigate(item.link)}
                  className={`cursor-pointer px-6 py-2 rounded-lg flex items-center justify-between text-lg ${
                    item.name === "Products"
                      ? "bg-gray-200 scale-90"
                      : "hover:bg-gray-200 hover:scale-90"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                  <FaChevronRight className="text-gray-600" />
                </li>
              ))}
            </ul>
          </nav>
          {/* Bottom Section */}
          <div className="mt-auto flex flex-col text-red-600">
            <ul>
              {[
                {
                  name: "Signout",
                  icon: (
                    <FaSignOutAlt
                      className="mr-2"
                      style={{ fontSize: "20px" }}
                    />
                  ),
                },
              ].map((item, idx) => (
                <li
                  key={idx}
                  onClick={() => navigate("/Logout")}
                  className="cursor-pointer px-6 py-2 hover:bg-gray-200 rounded-lg flex items-center justify-between text-lg"
                >
                  <div className="flex items-center space-x-2">
                    <span>{item.icon}</span>
                    <span>{item.name}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 p-4 md:p-6 overflow-y-auto">
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <FaBox className="text-gray-700 mr-2 text-2xl" />
              <span className="text-2xl font-bold text-gray-700">Products</span>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <button
                onClick={() => {
                  resetFormData();
                  setEditingProductId(null);
                  setShowAddCard(true);
                }}
                className="bg-gray-500 text-white py-2 px-12 rounded hover:bg-gray-600 whitespace-nowrap"
              >
                Add Product
              </button>
              <div className="w-full md:w-64">
                <input
                  placeholder="Search..."
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="border border-gray-400 focus:ring-gray-500 rounded p-2 w-full"
                />
              </div>
            </div>
          </div>

          {/* Global Modal for Messages */}
          {showModal && (
            <div
              className="fixed inset-0 z-[1000] bg-gray-400 bg-opacity-50 flex items-center justify-center"
              onClick={() => setShowModal(false)}
            >
              <div
                className="bg-white p-6 rounded shadow-md w-80"
                onClick={(e) => e.stopPropagation()}
              >
                <p
                  className={`text-center ${
                    modalType === "success" ? "text-black" : "text-red-500"
                  }`}
                >
                  {modalMessage}
                </p>
                <div className="mt-4 text-center">
                  <button
                    onClick={() => setShowModal(false)}
                    className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
                  >
                    {modalType === "success" ? "Done" : "Close"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <h3 className="text-lg font-bold mb-4">Confirm Deletion</h3>
                <p>
                  Are you sure you want to delete "{productToDelete?.name}"?
                </p>
                <div className="flex justify-end gap-4 mt-4">
                  <button
                    onClick={confirmDeleteProduct}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                  >
                    Delete
                  </button>
                  <button
                    onClick={cancelDelete}
                    className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Stepper Style Add/Edit Modal */}
          {showAddCard && (
            <div
              className="fixed inset-0 z-50 bg-gray-600 bg-opacity-50 flex items-center justify-center"
              onClick={() => setShowAddCard(false)}
            >
              <div
                className="bg-white p-6 rounded shadow-md w-11/12 md:w-3/4 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="text-3xl font-semibold mb-4">
                  {editingProductId ? "Edit Product" : "Add Product"}
                </h2>

                {/* Stepper navigation - unchanged */}
                <div className="flex items-center justify-between mb-6 px-2 md:px-0 overflow-x-auto whitespace-nowrap">
                  {/* Step 1: Product Info */}
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-1 sm:py-2 border rounded text-xs sm:text-base ${
                      currentStep === 1
                        ? "bg-blue-500 text-white"
                        : currentStep > 1
                        ? "bg-blue-200 text-gray-700"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    <span
                      className={`flex items-center justify-center w-5 h-5 rounded-full border-2 font-semibold mr-0 ${
                        currentStep === 1
                          ? "border-white text-white"
                          : "border-gray-700 text-gray-600"
                      }`}
                    >
                      1
                    </span>
                    <span className="hidden sm:inline">Product Info</span>
                  </button>
                  <div
                    className={`flex-1 h-1 mx-1 sm:mx-2 ${
                      currentStep > 1 ? "bg-blue-400" : "bg-gray-200"
                    }`}
                  />
                  {/* Step 2: Pricing */}
                  <button
                    type="button"
                    onClick={() => canAccessStep(2) && setCurrentStep(2)}
                    disabled={!canAccessStep(2)}
                    className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-1 sm:py-2 border rounded text-xs sm:text-base ${
                      currentStep === 2
                        ? "bg-blue-500 text-white"
                        : currentStep > 2
                        ? "bg-blue-200 text-gray-700"
                        : "bg-gray-200 text-gray-700"
                    } ${
                      !canAccessStep(2) ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <span
                      className={`flex items-center justify-center w-5 h-5 rounded-full border-2 font-semibold mr-0 ${
                        currentStep === 2
                          ? "border-white text-white"
                          : "border-gray-700 text-gray-600"
                      }`}
                    >
                      2
                    </span>
                    <span className="hidden sm:inline">Pricing</span>
                  </button>
                  <div
                    className={`flex-1 h-1 mx-1 sm:mx-2 ${
                      currentStep > 2 ? "bg-blue-400" : "bg-gray-200"
                    }`}
                  />
                  {/* Step 3: Images */}
                  <button
                    type="button"
                    onClick={() => canAccessStep(3) && setCurrentStep(3)}
                    disabled={!canAccessStep(3)}
                    className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-1 sm:py-2 border rounded text-xs sm:text-base ${
                      currentStep === 3
                        ? "bg-blue-500 text-white"
                        : currentStep > 3
                        ? "bg-blue-200 text-gray-700"
                        : "bg-gray-200 text-gray-700"
                    } ${
                      !canAccessStep(3) ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <span
                      className={`flex items-center justify-center w-5 h-5 rounded-full border-2 font-semibold mr-0 ${
                        currentStep === 3
                          ? "border-white text-white"
                          : "border-gray-700 text-gray-600"
                      }`}
                    >
                      3
                    </span>
                    <span className="hidden sm:inline">Images</span>
                  </button>
                  <div
                    className={`flex-1 h-1 mx-1 sm:mx-2 ${
                      currentStep > 3 ? "bg-blue-400" : "bg-gray-200"
                    }`}
                  />
                  {/* Step 4: Variants */}
                  <button
                    type="button"
                    onClick={() => canAccessStep(4) && setCurrentStep(4)}
                    disabled={!canAccessStep(4)}
                    className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-1 sm:py-2 border rounded text-xs sm:text-base ${
                      currentStep === 4
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    } ${
                      !canAccessStep(4) ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <span
                      className={`flex items-center justify-center w-5 h-5 rounded-full border-2 font-semibold mr-0 ${
                        currentStep === 4
                          ? "border-white text-white"
                          : "border-gray-700 text-gray-600"
                      }`}
                    >
                      4
                    </span>
                    <span className="hidden sm:inline">Variants</span>
                  </button>
                </div>

                {/* Modal Content */}
                <div className="min-h-[300px]">
                  {/* Step 1: Product Info */}
                  {currentStep === 1 && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mb-4">
                        <label className="block text-base font-medium sm:col-span-1 self-center">
                          Product Name
                        </label>
                        <input
                          placeholder="Enter product name"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="border border-gray-400 rounded p-2 sm:col-span-5"
                        />
                      </div>

                      {/* Category Field with Image */}
                      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mb-4 items-center">
                        <label className="block text-base font-medium sm:col-span-1">
                          Category
                        </label>
                        <div className="sm:col-span-3">
                          <input
                            placeholder="Enter category"
                            type="text"
                            name="category_name"
                            value={formData.category_name}
                            onChange={handleInputChange}
                            list="categories-list"
                            className="border border-gray-400 rounded p-2 w-full"
                          />
                          <datalist id="categories-list">
                            {categories.map((category, index) => (
                              <option
                                key={index}
                                value={category.category_name}
                              />
                            ))}
                          </datalist>
                        </div>
                        <div className="sm:col-span-2 flex items-center gap-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleCategoryImageUpload}
                            className="hidden"
                            id="category-image-input"
                          />
                          {categoryImagePreview ? (
                            <div className="flex items-center gap-2">
                              <img
                                src={categoryImagePreview}
                                alt="Category"
                                className="w-12 h-12 object-cover rounded border border-gray-300 cursor-pointer"
                                onClick={() =>
                                  document
                                    .getElementById("category-image-input")
                                    .click()
                                }
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setCategoryImagePreview("");
                                  setFormData((prev) => ({
                                    ...prev,
                                    category_image: null,
                                  }));
                                }}
                                className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                              >
                                <FaTimes className="text-xs" />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() =>
                                document
                                  .getElementById("category-image-input")
                                  .click()
                              }
                              className="border border-gray-400 rounded p-2 text-sm whitespace-nowrap"
                            >
                              Add Category Image
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mb-4">
                        <label className="block text-base font-medium sm:col-span-1 self-center">
                          Subcategory
                        </label>
                        <div className="relative sm:col-span-5">
                          <input
                            placeholder="Enter subcategory"
                            type="text"
                            name="subcategory_name"
                            value={formData.subcategory_name}
                            onChange={handleInputChange}
                            list="subcategories-list"
                            className="border border-gray-400 rounded p-2 w-full"
                          />
                          <datalist id="subcategories-list">
                            {subcategories.map((subcategory, index) => (
                              <option
                                key={subcategory.id || index}
                                value={subcategory.subcategory_name}
                              />
                            ))}
                          </datalist>
                        </div>
                      </div>

                      {/* Updated Rich Text Editor for Description */}
                      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mb-4">
                        <label className="block text-base font-medium sm:col-span-1 self-center">
                          Description
                        </label>
                        <div className="sm:col-span-5">
                          {/* Text Editor Toolbar */}
                          <div className="border border-gray-400 rounded-t bg-gray-50 p-2 flex flex-wrap gap-1">
                            {/* Text Formatting */}
                            <button
                              type="button"
                              onClick={() => executeCommand("bold")}
                              className="p-2 hover:bg-gray-200 rounded"
                              title="Bold"
                            >
                              <FaBold />
                            </button>
                            <button
                              type="button"
                              onClick={() => executeCommand("italic")}
                              className="p-2 hover:bg-gray-200 rounded"
                              title="Italic"
                            >
                              <FaItalic />
                            </button>
                            <button
                              type="button"
                              onClick={() => executeCommand("underline")}
                              className="p-2 hover:bg-gray-200 rounded"
                              title="Underline"
                            >
                              <FaUnderline />
                            </button>

                            {/* Lists */}
                            <button
                              type="button"
                              onClick={() =>
                                executeCommand("insertUnorderedList")
                              }
                              className="p-2 hover:bg-gray-200 rounded"
                              title="Bullet List"
                            >
                              <FaListUl />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                executeCommand("insertOrderedList")
                              }
                              className="p-2 hover:bg-gray-200 rounded"
                              title="Numbered List"
                            >
                              <FaListOl />
                            </button>

                            {/* Links & Images */}
                            <button
                              type="button"
                              onClick={createLink}
                              className="p-2 hover:bg-gray-200 rounded"
                              title="Insert Link"
                            >
                              <FaLink />
                            </button>
                            <button
                              type="button"
                              onClick={insertImage}
                              className="p-2 hover:bg-gray-200 rounded"
                              title="Insert Image"
                            >
                              <FaImage />
                            </button>
                          </div>

                          {/* Text Editor Content */}
                          <textarea
                            ref={editorRef}
                            value={formData.description}
                            onChange={handleEditorInput}
                            className="border border-gray-400 border-t-0 rounded-b p-4 h-48 resize-none overflow-y-auto focus:outline-none focus:ring-1 focus:ring-gray-500 w-full"
                            style={{ minHeight: "12rem", lineHeight: "1.4" }}
                            placeholder="Enter product description... 
Press Enter once for line break.
Press Enter twice for paragraph break."
                          />

                          {/* Character count */}
                          <div className="text-xs text-gray-500 mt-1 text-right">
                            {formData.description.length} characters
                          </div>

                         
                        </div>
                      </div>
                    </>
                  )}

                  {/* Steps 2, 3, 4 remain unchanged */}
                  {/* Step 2: Pricing */}
                  {currentStep === 2 && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mb-4">
                        <label className="block text-base font-medium sm:col-span-1 self-center">
                          Quantity
                        </label>
                        <input
                          placeholder="Enter quantity"
                          type="number"
                          name="stock_quantity"
                          value={formData.stock_quantity}
                          onChange={handleInputChange}
                          className="border border-gray-400 rounded p-2 sm:col-span-5"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mb-4">
                        <label className="block text-base font-medium sm:col-span-1 self-center">
                          Price
                        </label>
                        <input
                          placeholder="Enter price"
                          type="number"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          className="border border-gray-400 rounded p-2 sm:col-span-5"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-6 gap-4 mb-4">
                        <label className="block text-base font-medium sm:col-span-1 self-center">
                          Discount Price
                        </label>
                        <input
                          placeholder="Enter discount price"
                          type="number"
                          name="discounted_price"
                          value={formData.discounted_price}
                          onChange={handleInputChange}
                          className="border border-gray-400 rounded p-2 sm:col-span-5"
                        />
                      </div>
                    </>
                  )}

                  {/* Step 3: Images */}
                  {currentStep === 3 && (
                    <>
                      <div className="mb-6 border border-gray-300 p-4 rounded">
                        <h4 className="text-xl font-semibold mb-2">
                          Product Images<span className="text-red-500">*</span>
                        </h4>
                        <div className="flex items-center gap-4 flex-wrap">
                          <label className="text-base font-medium whitespace-nowrap">
                            Product Images
                          </label>
                          <input
                            type="file"
                            name="images[]"
                            multiple
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="border border-gray-400 rounded p-2 flex-1 min-w-[150px]"
                          />
                        </div>
                        {imagePreviews.length > 0 && (
                          <div className="flex flex-wrap gap-4 mt-4">
                            {imagePreviews.map((img, index) => (
                              <div key={index} className="relative w-12 h-12">
                                <img
                                  src={img.preview}
                                  alt="Selected"
                                  className="w-full h-full object-cover rounded border border-gray-300"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeImage(index)}
                                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                                >
                                  <FaTimes className="text-sm" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Step 4: Variants */}
                  {currentStep === 4 && (
                    <>
                      <div className="mb-4">
                        <h4 className="text-xl font-semibold mb-2">
                          Product Variants (Optional)
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">
                          Add variant combinations with multiple attributes. For
                          Color attributes, you can optionally upload an image.
                        </p>

                        {formData.combinations.map((combination, combIndex) => (
                          <div
                            key={combIndex}
                            className="border border-gray-300 p-4 rounded mb-4"
                          >
                            <div className="flex justify-between items-center mb-4">
                              <h5 className="text-lg font-medium">
                                Variant {combIndex + 1}
                              </h5>
                              <button
                                type="button"
                                onClick={() => removeCombination(combIndex)}
                                className="bg-red-500 text-white py-1 px-3 rounded hover:bg-red-600 text-sm"
                              >
                                Remove Variant
                              </button>
                            </div>

                            {/* Attributes Section */}
                            <div className="mb-4">
                              <div className="flex justify-between items-center mb-2">
                                <h6 className="font-medium">Attributes</h6>
                                <button
                                  type="button"
                                  onClick={() =>
                                    addAttributeToCombination(combIndex)
                                  }
                                  className="bg-green-500 text-white py-1 px-3 rounded hover:bg-green-600 text-sm"
                                >
                                  Add Attribute
                                </button>
                              </div>

                              {combination.attributes &&
                                combination.attributes.map(
                                  (attribute, attrIndex) => (
                                    <div
                                      key={attrIndex}
                                      className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-3 p-3 border border-gray-200 rounded"
                                    >
                                      {/* Attribute Name */}
                                      <div className="md:col-span-3">
                                        <label className="block text-sm font-medium mb-1">
                                          Attribute Name
                                        </label>
                                        <input
                                          type="text"
                                          placeholder="e.g., Color, Size"
                                          value={attribute.name}
                                          onChange={(e) =>
                                            handleAttributeChange(
                                              combIndex,
                                              attrIndex,
                                              "name",
                                              e.target.value
                                            )
                                          }
                                          className="border border-gray-400 rounded p-2 w-full"
                                        />
                                      </div>

                                      {/* Attribute Value */}
                                      <div className="md:col-span-3">
                                        <label className="block text-sm font-medium mb-1">
                                          Attribute Value
                                        </label>
                                        <input
                                          type="text"
                                          placeholder="e.g., Red, Large"
                                          value={attribute.value}
                                          onChange={(e) =>
                                            handleAttributeChange(
                                              combIndex,
                                              attrIndex,
                                              "value",
                                              e.target.value
                                            )
                                          }
                                          className="border border-gray-400 rounded p-2 w-full"
                                        />
                                      </div>

                                      {/* Image Upload for Color Attribute */}
                                      {["color", "colour"].includes(
                                        attribute.name.toLowerCase()
                                      ) && (
                                        <div className="md:col-span-4">
                                          <label className="block text-sm font-medium mb-1">
                                            Color Image (Optional)
                                          </label>
                                          <div className="flex items-center gap-2">
                                            <input
                                              type="file"
                                              accept="image/*"
                                              onChange={(e) =>
                                                handleColorImageUpload(
                                                  combIndex,
                                                  attrIndex,
                                                  e
                                                )
                                              }
                                              className="hidden"
                                              id={`color-image-${combIndex}-${attrIndex}`}
                                            />
                                            {attribute.color_image_preview ? (
                                              <div className="flex items-center gap-2">
                                                <img
                                                  src={
                                                    attribute.color_image_preview
                                                  }
                                                  alt="Color"
                                                  className="w-10 h-10 object-cover rounded border border-gray-300 cursor-pointer"
                                                  onClick={() =>
                                                    document
                                                      .getElementById(
                                                        `color-image-${combIndex}-${attrIndex}`
                                                      )
                                                      .click()
                                                  }
                                                />
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    removeColorImage(
                                                      combIndex,
                                                      attrIndex
                                                    )
                                                  }
                                                  className="bg-red-500 text-white p-1 rounded hover:bg-red-600"
                                                >
                                                  <FaTimes className="text-xs" />
                                                </button>
                                              </div>
                                            ) : (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  document
                                                    .getElementById(
                                                      `color-image-${combIndex}-${attrIndex}`
                                                    )
                                                    .click()
                                                }
                                                className="border border-gray-400 rounded p-2 text-sm whitespace-nowrap"
                                              >
                                                Add Image
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Remove Attribute Button */}
                                      <div className="md:col-span-2 flex items-end">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            removeAttributeFromCombination(
                                              combIndex,
                                              attrIndex
                                            )
                                          }
                                          className="bg-red-500 text-white p-2 rounded hover:bg-red-600"
                                        >
                                          <FaTimes className="text-sm" />
                                        </button>
                                      </div>
                                    </div>
                                  )
                                )}
                            </div>

                            {/* Pricing and Stock Section */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                              {/* Price */}
                              <div>
                                <label className="block text-sm font-medium mb-1">
                                  Price
                                </label>
                                <input
                                  type="number"
                                  placeholder="Price"
                                  value={combination.price}
                                  onChange={(e) =>
                                    handleCombinationChange(
                                      combIndex,
                                      "price",
                                      e.target.value
                                    )
                                  }
                                  className="border border-gray-400 rounded p-2 w-full"
                                />
                              </div>

                              {/* Discount Price */}
                              <div>
                                <label className="block text-sm font-medium mb-1">
                                  Discount Price
                                </label>
                                <input
                                  type="number"
                                  placeholder="Discount Price"
                                  value={combination.discounted_price}
                                  onChange={(e) =>
                                    handleCombinationChange(
                                      combIndex,
                                      "discounted_price",
                                      e.target.value
                                    )
                                  }
                                  className="border border-gray-400 rounded p-2 w-full"
                                />
                              </div>

                              {/* Stock Quantity */}
                              <div>
                                <label className="block text-sm font-medium mb-1">
                                  Stock Quantity
                                </label>
                                <input
                                  type="number"
                                  placeholder="Stock"
                                  value={combination.stock_quantity}
                                  onChange={(e) =>
                                    handleCombinationChange(
                                      combIndex,
                                      "stock_quantity",
                                      e.target.value
                                    )
                                  }
                                  className="border border-gray-400 rounded p-2 w-full"
                                />
                              </div>

                              {/* SKU */}
                              <div>
                                <label className="block text-sm font-medium mb-1">
                                  SKU (Optional)
                                </label>
                                <input
                                  type="text"
                                  placeholder="SKU"
                                  value={combination.sku}
                                  onChange={(e) =>
                                    handleCombinationChange(
                                      combIndex,
                                      "sku",
                                      e.target.value
                                    )
                                  }
                                  className="border border-gray-400 rounded p-2 w-full"
                                />
                              </div>
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={addCombination}
                          className="bg-blue-500 text-white py-2 px-6 rounded hover:bg-blue-600"
                        >
                          Add Variant
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-end gap-4 mt-4">
                  <button
                    onClick={() => setShowAddCard(false)}
                    className="bg-gray-500 text-white py-2 px-5 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  {currentStep < 4 && (
                    <button
                      disabled={!validateStep(currentStep)}
                      onClick={() => setCurrentStep(currentStep + 1)}
                      className="bg-blue-500 text-white py-2 px-5 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  )}
                  {currentStep === 4 && (
                    <button
                      onClick={editingProductId ? handleUpdate : handleAdd}
                      className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
                    >
                      {editingProductId ? "Update" : "Add"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Product Details Modal */}
          {viewProduct && (
            <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
              <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-4xl relative overflow-y-auto max-h-[90vh]">
                <button
                  onClick={closeViewModal}
                  className="absolute top-4 right-5 text-red-500 hover:text-red-700"
                >
                  <FaTimes className="text-3xl" />
                </button>
                <h3 className="text-2xl font-bold mb-4">
                  Product Details for "{viewProduct.name}"
                </h3>

                {/* Category Image */}
                {viewProduct.category_image && (
                  <div className="mb-4">
                    <h4 className="text-lg font-semibold mb-2">
                      Category Image
                    </h4>
                    <img
                      src={`${API_URL}/storage/${viewProduct.category_image}`}
                      alt="Category"
                      className="w-24 h-24 object-cover rounded border border-gray-300"
                    />
                  </div>
                )}

                {/* Product Images */}
                {viewProduct.images && viewProduct.images.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-lg font-semibold mb-2">
                      Product Images
                    </h4>
                    <div className="flex space-x-2 overflow-x-auto p-2 border border-gray-300 rounded-lg">
                      {viewProduct.images.map((image, idx) => (
                        <img
                          key={idx}
                          src={`${API_URL}/storage/${image}`}
                          alt={`Product ${idx + 1}`}
                          className="w-48 h-32 object-cover cursor-pointer border border-gray-300"
                          onClick={() => setSelectedImage(image)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Color Images */}
                {viewProduct.color_images &&
                  Object.keys(viewProduct.color_images).length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold mb-2">
                        Color Images
                      </h4>
                      <div className="flex space-x-2 overflow-x-auto p-2 border border-gray-300 rounded-lg">
                        {Object.entries(viewProduct.color_images).map(
                          ([colorName, colorData], idx) => (
                            <div key={idx} className="text-center">
                              <img
                                src={`${API_URL}/storage/${colorData.image_path}`}
                                alt={`${colorName} color`}
                                className="w-24 h-24 object-cover cursor-pointer border border-gray-300"
                                onClick={() =>
                                  setSelectedImage(colorData.image_path)
                                }
                              />
                              <p className="text-sm mt-1 capitalize">
                                {colorName}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                {/* Product Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p>
                      <strong>Name:</strong> {viewProduct.name}
                    </p>
                    <p>
                      <strong>Category:</strong> {viewProduct.category}
                    </p>
                    <p>
                      <strong>Subcategory:</strong> {viewProduct.subcategory}
                    </p>
                    <p>
                      <strong>Base Quantity:</strong>{" "}
                      {viewProduct.stock_quantity}
                    </p>
                  </div>
                  <div>
                    <p>
                      <strong>Base Price:</strong> {viewProduct.price}
                    </p>
                    <p>
                      <strong>Base Discount Price:</strong>{" "}
                      {viewProduct.discounted_price}
                    </p>
                    <p>
                      <strong>Status:</strong> {viewProduct.status}
                    </p>
                    <p>
                      <strong>Has Variants:</strong>{" "}
                      {viewProduct.has_variants ? "Yes" : "No"}
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <p>
                    <strong>Description:</strong>
                  </p>
                  {/* Display HTML description with preserved formatting */}
                  <div
                    className="mt-2 prose max-w-none"
                    style={{ whiteSpace: "pre-line", lineHeight: "1.6" }}
                    dangerouslySetInnerHTML={{
                      __html: viewProduct.description,
                    }}
                  />
                </div>

                {/* Variants */}
                {viewProduct.has_variants && viewProduct.variants && (
                  <div className="mt-6">
                    <h4 className="text-lg font-semibold mb-2">Variants</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full border-collapse border border-gray-300">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 px-4 py-2">
                              Attributes
                            </th>
                            <th className="border border-gray-300 px-4 py-2">
                              Price
                            </th>
                            <th className="border border-gray-300 px-4 py-2">
                              Discounted Price
                            </th>
                            <th className="border border-gray-300 px-4 py-2">
                              Stock
                            </th>
                            <th className="border border-gray-300 px-4 py-2">
                              SKU
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewProduct.variants.map((variant, idx) => (
                            <tr key={idx}>
                              <td className="border border-gray-300 px-4 py-2">
                                {variant.attributes &&
                                  Object.entries(variant.attributes).map(
                                    ([key, value]) => (
                                      <div key={key}>
                                        <strong>{key}:</strong> {value}
                                      </div>
                                    )
                                  )}
                              </td>
                              <td className="border border-gray-300 px-4 py-2">
                                {variant.price}
                              </td>
                              <td className="border border-gray-300 px-4 py-2">
                                {variant.discounted_price}
                              </td>
                              <td className="border border-gray-300 px-4 py-2">
                                {variant.stock_quantity}
                              </td>
                              <td className="border border-gray-300 px-4 py-2">
                                {variant.sku || "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Selected Image Modal */}
          {selectedImage && (
            <div
              className="fixed inset-0 z-[1000] bg-black bg-opacity-70 flex items-center justify-center"
              onClick={() => setSelectedImage(null)}
            >
              <div
                className="bg-white p-4 rounded shadow-lg w-11/12 sm:w-3/4 md:w-1/2 lg:w-1/3"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={
                    selectedImage.startsWith("http")
                      ? selectedImage
                      : `${API_URL}/storage/${selectedImage}`
                  }
                  alt="Selected"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          )}

          {/* Products Table */}
          <div className="overflow-x-auto">
            <table className="table-auto w-full border-collapse border border-gray-400">
              <thead>
                <tr>
                  <th className="px-5 py-2 border-b bg-gray-200 text-gray-700 text-left">
                    ID
                  </th>
                  <th className="px-5 py-2 border-b bg-gray-200 text-gray-700 text-left">
                    Product
                  </th>
                  <th className="px-5 py-2 border-b bg-gray-200 text-gray-700 text-left">
                    Category
                  </th>
                  <th className="px-5 py-2 border-b bg-gray-200 text-gray-700 text-left">
                    Quantity
                  </th>
                  <th className="px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">
                    Price
                  </th>
                  <th className="px-8 py-2 border-b bg-gray-200 text-gray-700 text-left">
                    Discount Price
                  </th>
                  <th className="px-10 py-2 border-b bg-gray-200 text-gray-700 text-left">
                    Details
                  </th>
                  <th className="px-30 py-2 border-b bg-gray-200 text-gray-700 text-left">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((product, index) => {
                  const overallIndex = indexOfFirstItem + index;
                  const productKey = product.id ? product.id : overallIndex;
                  return (
                    <tr key={productKey} className="border-b hover:bg-gray-50">
                      <td className="px-5 py-2 text-gray-700">
                        {product.id || overallIndex}
                      </td>
                      <td className="px-10 py-2 text-gray-700">
                        {product.name}
                      </td>
                      <td className="px-10 py-2 text-gray-700">
                        {product.category || product.category_name}
                      </td>
                      <td className="px-10 py-2 text-gray-700">
                        {product.stock_quantity}
                      </td>
                      <td className="px-10 py-2 text-gray-700">
                        {product.price}
                      </td>
                      <td className="px-8 py-2 text-gray-700">
                        {product.discounted_price}
                      </td>
                      <td className="px-10 py-2">
                        <button
                          onClick={() => handleViewValues(product)}
                          className="text-blue-500 py-1 px-0 rounded hover:text-blue-800"
                        >
                          View More
                        </button>
                      </td>
                      <td className="px-30 py-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="bg-blue-500 text-white py-1 px-3 rounded hover:bg-blue-600 mr-2"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(product)}
                          className="bg-red-500 text-white py-1 px-3 rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="mt-4 flex justify-center items-center gap-4">
            <button
              className={`px-4 py-2 rounded-md ${
                currentPage === 1
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-gray-500 text-white hover:bg-gray-600"
              }`}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              Previous
            </button>
            <span className="text-black">
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              className={`px-4 py-2 rounded-md ${
                currentPage === totalPages || totalPages === 0
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-gray-500 text-white hover:bg-gray-600"
              }`}
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Products;
